/**
 * L'attuazione di `GatewayPort` che parla col gateway, in HTTP.
 *
 * Due livelli, con confini netti (05-CONTRATTO-API §1): il **livello GateMesh**
 * (`/api/v1/app/*`) per accesso, dati della linea ed eventi; il **livello compatibile**
 * (`player_api.php`, `/live/…`, `/catchup/…`) per il catalogo e la consegna, che va già
 * bene ed è la parte pesante.
 *
 * **Le due forme di credenziale funzionano su tutto, dal 28-09-2026.** `player_api.php` accetta il
 * gettone, quindi catalogo, categorie, film, serie e guida si leggono con la forma nativa; e la
 * consegna passa dalle rotte `/api/v1/app/live` e `/api/v1/app/catchup`, che prendono le credenziali
 * dalla query — il livello compatibile le vuole nel percorso, e un percorso col gettone non si sa
 * scrivere. Vedi `liveStreamUrl`.
 *
 * Tre regole di 07-PRESTAZIONI §7 sono qui dentro e non altrove:
 *  - **tempo massimo esplicito su ogni chiamata**, diverso per tipo — breve per lo stato,
 *    lungo per il catalogo. Una chiamata senza tempo massimo è un'app che si pianta;
 *  - **le richieste si annullano** quando si cambia schermata (il `CancelSignal`);
 *  - l'elenco dei canali si conserva con `ETag` e non si riscarica a ogni apertura.
 */

import {
  PlayerError,
  TEXT_LOCALES,
  credentialsQuery,
  parseGatewayError,
  type Channel,
  type Credentials,
  type MovieDetail,
  type Program,
  type SeriesDetail,
  type LocalizedText,
  type SeriesSeason,
  type SportEvent,
  type EpisodeInfo,
  type VodItem,
  type VodKind,
} from '@player/domain';
import type {
  AggregatorContentResult,
  CancelSignal,
  CatalogResult,
  ChannelCategory,
  EventsResult,
  GatewayPort,
  GuideWindowRequest,
  LoginResult,
  Storefront,
  StorefrontAggregator,
  StorefrontHero,
  StorefrontModule,
} from '@player/ports';

/** Tempi massimi, in millisecondi, per tipo di chiamata. */
export const TIMEOUTS_MS = {
  /** Stato: deve fallire in fretta, perché blocca l'ingresso. */
  status: 8_000,
  /** Catalogo: 4,91 MB su un catalogo reale, su un Wi-Fi di casa. */
  catalog: 45_000,
  guide: 20_000,
  data: 10_000,
} as const;

export interface HttpGatewayOptions {
  /** Iniettato invece che preso dal globale: è ciò che rende l'adattatore provabile
   *  senza rete, e sostituibile dove `fetch` non c'è. */
  readonly fetchImpl: typeof fetch;
  readonly deviceId: string;
  /** Versione dell'app, per la diagnosi lato gateway. */
  readonly appVersion: string;
}

function errorFor(
  code: string,
  retryable: boolean,
  params: Record<string, string | number> = {},
): PlayerError {
  return new PlayerError({ code, params, retryable, atMs: Date.now() });
}

function fail(code: string, retryable: boolean, params: Record<string, string | number> = {}): never {
  throw errorFor(code, retryable, params);
}

export class HttpGatewayClient implements GatewayPort {
  constructor(private readonly options: HttpGatewayOptions) {}

  private buildUrl(
    credentials: Credentials,
    path: string,
    extra: ReadonlyArray<readonly [string, string]> = [],
  ): string {
    const url = new URL(path, `${credentials.host}/`);
    for (const [key, value] of credentialsQuery(credentials)) url.searchParams.set(key, value);
    url.searchParams.set('device_id', this.options.deviceId);
    for (const [key, value] of extra) url.searchParams.set(key, value);
    return url.toString();
  }

  /**
   * Una richiesta con tempo massimo e annullamento.
   *
   * Un corpo che non è JSON, o un errore di rete, **non** diventano un errore generico
   * muto: diventano un codice che si vede. Nessun `catch {}` che ingoia.
   */
  private async request(
    url: string,
    timeoutMs: number,
    init: { method?: string; body?: string; headers?: Record<string, string> },
    signal: CancelSignal | undefined,
  ): Promise<{ status: number; body: unknown; etag: string | null }> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const onAbort = (): void => controller.abort();
    if (signal) {
      if (signal.aborted) controller.abort();
      else signal.addEventListener('abort', onAbort);
    }

    try {
      const response = await this.options.fetchImpl(url, {
        method: init.method ?? 'GET',
        body: init.body,
        headers: { 'x-player-app-version': this.options.appVersion, ...(init.headers ?? {}) },
        signal: controller.signal,
      });
      const etag = response.headers.get('etag');
      if (response.status === 304) return { status: 304, body: null, etag };
      const text = await response.text();
      let body: unknown = null;
      if (text.length > 0) {
        try {
          body = JSON.parse(text);
        } catch {
          /*
            Il corpo non è JSON. Non si indovina: si dice — e si dice **quale** dei due casi è.

            Una pagina HTML non è una risposta malformata di un gateway: è la risposta di qualcosa
            che gateway non è. Un server di sviluppo senza proxy, la pagina d'errore di un reverse
            proxy, un portale di rete, il pannello al posto dell'API. Chiamarla «malformata»
            manderebbe a cercare un difetto nel gateway, che è il posto sbagliato.
          */
          if (looksLikeHtml(text)) {
            fail('app.not_a_gateway', true, { status: response.status });
          }
          fail('app.malformed_response', true, { status: response.status });
        }
      }
      return { status: response.status, body, etag };
    } catch (error) {
      if (error instanceof PlayerError) throw error;
      if (controller.signal.aborted && signal?.aborted) throw error;
      if (controller.signal.aborted) fail('device.request_timeout', true, { timeoutMs });
      // Rete assente o host irraggiungibile. **Non è un rifiuto delle credenziali**
      // (02-ACCESSO §4.2): non butta fuori nessuno dalla sessione.
      //
      // `throw` esplicito e non `fail(…)`: dentro un `try` con un `finally`, TypeScript non
      // riconosce che una funzione dichiarata `never` chiude il percorso, e chiedeva un
      // `return` finale in fondo a un blocco che non torna mai. Scrivere `throw` dice la
      // stessa cosa al compilatore e a chi legge, invece di zittirlo con un `return`
      // irraggiungibile.
      throw errorFor('gateway.unreachable', true, {});
    } finally {
      clearTimeout(timer);
      if (signal) signal.removeEventListener('abort', onAbort);
    }
  }

  private ensureOk(status: number, body: unknown): void {
    if (status >= 200 && status < 300) return;
    throw new PlayerError(parseGatewayError(body, Date.now()));
  }

  // ── Livello GateMesh ───────────────────────────────────────────────────────

  async login(credentials: Credentials, signal?: CancelSignal): Promise<LoginResult> {
    return this.loginLike(credentials, 'api/v1/app/login', signal);
  }

  async me(credentials: Credentials, signal?: CancelSignal): Promise<LoginResult> {
    return this.loginLike(credentials, 'api/v1/app/me', signal);
  }

  private async loginLike(
    credentials: Credentials,
    path: string,
    signal?: CancelSignal,
  ): Promise<LoginResult> {
    const { status, body } = await this.request(
      this.buildUrl(credentials, path),
      TIMEOUTS_MS.status,
      {},
      signal,
    );
    this.ensureOk(status, body);
    return parseLoginResult(body);
  }

  async listEvents(
    credentials: Credentials,
    kind: string,
    signal?: CancelSignal,
  ): Promise<EventsResult> {
    const { status, body } = await this.request(
      this.buildUrl(credentials, `api/v1/app/events/${encodeURIComponent(kind)}`),
      TIMEOUTS_MS.data,
      {},
      signal,
    );
    this.ensureOk(status, body);
    return parseEventsResult(body);
  }

  /**
   * Il contenuto di un aggregatore, già valutato dal server per questa linea.
   *
   * L'app non riceve la regola e non la valuta: la divisione fra «in diretta» e «più tardi» la fa da
   * sé con `upcomingEvents(events, nowMs)`, che è una cosa diversa — l'appartenenza all'aggregatore
   * l'ha decisa il server, lo stato che si vede lo decide chi guarda lo schermo.
   */
  async readAggregator(
    credentials: Credentials,
    aggregatorId: string,
    signal?: CancelSignal,
  ): Promise<AggregatorContentResult> {
    const { status, body } = await this.request(
      this.buildUrl(credentials, `api/v1/app/aggregators/${encodeURIComponent(aggregatorId)}`),
      TIMEOUTS_MS.data,
      {},
      signal,
    );
    this.ensureOk(status, body);
    return parseAggregatorContent(body);
  }

  /**
   * La vetrina: quali fasce, in che ordine, e con quali aggregatori.
   *
   * Non il contenuto — quello arriva dalle chiamate di catalogo che il player fa comunque.
   */
  async readStorefront(credentials: Credentials, signal?: CancelSignal): Promise<Storefront> {
    const { status, body } = await this.request(
      this.buildUrl(credentials, 'api/v1/app/storefront'),
      TIMEOUTS_MS.data,
      {},
      signal,
    );
    this.ensureOk(status, body);
    return parseStorefront(body);
  }

  // ── Livello compatibile ────────────────────────────────────────────────────

  async listChannels(
    credentials: Credentials,
    knownEtag: string | null,
    signal?: CancelSignal,
  ): Promise<CatalogResult> {
    const { status, body, etag } = await this.request(
      this.buildUrl(credentials, 'player_api.php', [['action', 'get_live_streams']]),
      TIMEOUTS_MS.catalog,
      knownEtag ? { headers: { 'if-none-match': knownEtag } } : {},
      signal,
    );
    if (status === 304) return { channels: [], etag: knownEtag, notModified: true };
    this.ensureOk(status, body);
    return { channels: parseChannels(body), etag, notModified: false };
  }

  async listCategories(
    credentials: Credentials,
    signal?: CancelSignal,
  ): Promise<ReadonlyArray<ChannelCategory>> {
    const { status, body } = await this.request(
      this.buildUrl(credentials, 'player_api.php', [['action', 'get_live_categories']]),
      TIMEOUTS_MS.catalog,
      {},
      signal,
    );
    this.ensureOk(status, body);
    return parseCategories(body);
  }

  async listVod(
    credentials: Credentials,
    kind: VodKind,
    signal?: CancelSignal,
  ): Promise<ReadonlyArray<VodItem>> {
    const action = kind === 'movie' ? 'get_vod_streams' : 'get_series';
    const { status, body } = await this.request(
      this.buildUrl(credentials, 'player_api.php', [['action', action]]),
      TIMEOUTS_MS.catalog,
      {},
      signal,
    );
    this.ensureOk(status, body);
    return kind === 'movie' ? parseMovies(body) : parseSeriesList(body);
  }

  async listVodCategories(
    credentials: Credentials,
    kind: VodKind,
    signal?: CancelSignal,
  ): Promise<ReadonlyArray<ChannelCategory>> {
    const action = kind === 'movie' ? 'get_vod_categories' : 'get_series_categories';
    const { status, body } = await this.request(
      this.buildUrl(credentials, 'player_api.php', [['action', action]]),
      TIMEOUTS_MS.catalog,
      {},
      signal,
    );
    this.ensureOk(status, body);
    return parseCategories(body);
  }

  async readSeries(
    credentials: Credentials,
    seriesId: string,
    signal?: CancelSignal,
  ): Promise<SeriesDetail> {
    const { status, body } = await this.request(
      this.buildUrl(credentials, 'player_api.php', [
        ['action', 'get_series_info'],
        ['series_id', seriesId],
      ]),
      TIMEOUTS_MS.guide,
      {},
      signal,
    );
    this.ensureOk(status, body);
    return parseSeriesDetail(body, seriesId);
  }

  async readMovie(
    credentials: Credentials,
    movieId: string,
    signal?: CancelSignal,
  ): Promise<MovieDetail> {
    const { status, body } = await this.request(
      this.buildUrl(credentials, 'player_api.php', [
        ['action', 'get_vod_info'],
        ['vod_id', movieId],
      ]),
      TIMEOUTS_MS.guide,
      {},
      signal,
    );
    this.ensureOk(status, body);
    return parseMovieDetail(body, movieId);
  }

  /**
   * Un film o un episodio, **sulla rotta che si chiama `live`**.
   *
   * Non è una svista e non è una scorciatoia: dietro `/api/v1/app/live/:uuid` il gateway chiama
   * `deliverToAuthorizedLine`, che dopo i controlli d'accesso distingue da sé un canale da un
   * contenuto on demand — un film non ha sessione e non ne avrà mai, e la risposta arriva
   * dall'indice degli asset invece che dall'instradamento verso un worker. La rotta non decide cosa
   * sia l'identificativo che le si passa; lo decide quel che l'identificativo è.
   *
   * L'alternativa era il livello compatibile — `/vod/<utente>/<password>/<id>` — che però porta le
   * credenziali **nel percorso**, e con un gettone quel percorso non si sa scrivere: è esattamente il
   * motivo per cui esistono le rotte `/api/v1/app/*` di consegna.
   *
   * Resta da chiedere al gateway un alias `/api/v1/app/vod/:uuid` che punti allo stesso corpo, per
   * ragioni di leggibilità dei log: oggi una riga di accesso a un film dice «live». Fino a quel
   * momento questa riga è il posto dove sta scritto perché, invece di essere una sorpresa per chi
   * legge i log.
   *
   * ## Niente `.m3u8`, e questo è il difetto che impediva di vedere un film
   *
   * Questa funzione chiamava `liveStreamUrl`, che aggiunge `.m3u8` perché un canale **è** un
   * manifesto. Un film non lo è: è un file, e il gateway lo consegna a intervalli di byte. Il gateway
   * taglia l'estensione prima di cercare l'identificativo, quindi l'accesso funzionava e nessuno
   * protestava — ma l'indirizzo diceva «questo è HLS», e l'app si comportava di conseguenza: dava un
   * MP4 in pasto a hls.js, che cercava un manifesto, non lo trovava e dichiarava un errore di rete.
   *
   * Una bugia scritta da noi, accettata in silenzio da chi la riceveva, e pagata tre strati più in
   * là da chi guardava lo schermo.
   */
  vodStreamUrl(credentials: Credentials, contentId: string): string {
    const url = new URL(`api/v1/app/live/${encodeURIComponent(contentId)}`, `${credentials.host}/`);
    for (const [key, value] of credentialsQuery(credentials)) url.searchParams.set(key, value);
    return url.toString();
  }

  async loadGuideWindow(
    credentials: Credentials,
    request: GuideWindowRequest,
    signal?: CancelSignal,
  ): Promise<ReadonlyArray<Program>> {
    if (request.channelIds.length === 0) return [];

    /*
      **Una richiesta per tutti i canali della finestra, non una per canale.**

      Chiedere `get_simple_data_table` in ciclo costava una richiesta per riga visibile: con dodici
      righe, dodici richieste in fila a ogni sosta dello scorrimento. Contro il tetto d'ingresso del
      gateway — cento ogni quarto d'ora per indirizzo — erano **otto soste prima del blocco**, e chi
      sfogliava la guida si bloccava da solo per un quarto d'ora.

      Era anche il motivo per cui la guida si disegnava piano: dodici andate e ritorno in sequenza,
      ognuna con la sua latenza, prima che comparisse una riga.

      `get_guide_window` è nostra, non dello standard: `stream_ids` separati da virgola, `from` e
      `to` in secondi unix. Il backend intersecerà comunque con i canali che la linea possiede.
    */
    const { status, body } = await this.request(
      this.buildUrl(credentials, 'player_api.php', [
        ['action', 'get_guide_window'],
        ['stream_ids', request.channelIds.join(',')],
        ['from', String(Math.floor(request.fromMs / 1000))],
        ['to', String(Math.ceil(request.toMs / 1000))],
      ]),
      TIMEOUTS_MS.guide,
      {},
      signal,
    );
    this.ensureOk(status, body);

    const programs: Program[] = [];
    for (const program of parseGuideWindow(body)) {
      if (program.endMs > request.fromMs && program.startMs < request.toMs) programs.push(program);
    }
    return programs;
  }

  /**
   * La diretta, sulle rotte di consegna del livello GateMesh.
   *
   * **Una rotta per entrambe le forme di credenziale**, e non `/live/<utente>/<password>/<id>` per la
   * coppia e questa per il gettone. Il livello compatibile porta le credenziali nel *percorso*, e con
   * un gettone quel percorso non si sa scrivere: il gateway ha quindi una rotta che le prende dalla
   * query, e quella rotta accetta entrambe le forme. Tenerne due qui vorrebbe dire due strade da
   * provare e da tenere allineate per una differenza che non serve a nessuno — il livello compatibile
   * resta intatto per i lettori di terze parti, non perché ci passi anche la nostra app.
   *
   * Dietro, la rotta nuova chiama lo stesso corpo di `/live/...`: stessi controlli, stessi limiti,
   * stessa 302 verso la sessione. Non è una consegna diversa, è un modo diverso di presentarsi.
   */
  liveStreamUrl(credentials: Credentials, channelId: string): string {
    const url = new URL(`api/v1/app/live/${encodeURIComponent(channelId)}.m3u8`, `${credentials.host}/`);
    for (const [key, value] of credentialsQuery(credentials)) url.searchParams.set(key, value);
    return url.toString();
  }

  /**
   * L'archivio, e `start` è **un unix in secondi**.
   *
   * Prima componeva `?start=2026-09-27-11:30`, che non è nessuno dei due formati che il livello
   * compatibile accetta: quella rotta vuole un unix, la grafia `/timeshift/...` vuole
   * `AAAA-MM-GG:hh-mm`. Il valore era doppiamente sbagliato — formato inventato, e mandato alla
   * rotta che ne voleva l'altro — e non se ne era accorto nessuno perché la riproduzione non era
   * mai stata provata. Un numero è la forma che non si può sbagliare, e l'app l'istante ce l'ha già.
   */
  catchupStreamUrl(credentials: Credentials, channelId: string, startMs: number): string {
    const url = new URL(
      `api/v1/app/catchup/${encodeURIComponent(channelId)}/playlist.m3u8`,
      `${credentials.host}/`,
    );
    for (const [key, value] of credentialsQuery(credentials)) url.searchParams.set(key, value);
    url.searchParams.set('start', String(Math.floor(startMs / 1000)));
    return url.toString();
  }
}

// ── Lettura delle risposte ───────────────────────────────────────────────────
//
// Ogni lettore rifiuta invece di indovinare. Un campo che manca è un errore che si vede,
// non uno zero che si propaga.

function asRecord(value: unknown): Record<string, unknown> {
  if (typeof value !== 'object' || value === null) fail('app.malformed_response', true, {});
  return value as Record<string, unknown>;
}

function requireNumber(source: Record<string, unknown>, key: string): number {
  const value = source[key];
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  fail('app.malformed_response', true, { field: key });
}

export function parseLoginResult(body: unknown): LoginResult {
  const root = asRecord(body);
  const line = asRecord(root['line']);
  const gateway = asRecord(root['gateway']);
  const expiresAt = line['expiresAt'];
  return {
    line: {
      expiresAtMs: typeof expiresAt === 'string' ? Date.parse(expiresAt) : null,
      maxConnections: requireNumber(line, 'maxConnections'),
      maxConnectionsPerIp:
        typeof line['maxConnectionsPerIp'] === 'number' ? line['maxConnectionsPerIp'] : null,
      activeConnections: requireNumber(line, 'activeConnections'),
    },
    gateway: {
      id: typeof gateway['id'] === 'string' ? gateway['id'] : '',
      version: typeof gateway['version'] === 'string' ? gateway['version'] : '',
    },
    serverNowMs: readServerNow(root),
  };
}

/**
 * L'istante del server. Senza, «è in diretta» non si può decidere, e **non si ripiega
 * sull'orologio dell'apparecchio**: un box da quaranta euro con l'orologio sbagliato di
 * ore metterebbe tutto nella fascia sbagliata, e da qui non ci sarebbe modo di
 * accorgersene (05-CONTRATTO-API §6.1).
 */
function readServerNow(root: Record<string, unknown>): number {
  const now = root['now'];
  if (typeof now === 'number' && Number.isFinite(now)) return now;
  if (typeof now === 'string') {
    const parsed = Date.parse(now);
    if (Number.isFinite(parsed)) return parsed;
  }
  fail('app.malformed_response', true, { field: 'now' });
}

/**
 * I canali del catalogo di un evento: **annidati**, e questa funzione esiste per un difetto.
 *
 * Il server pubblica `channels: [{ name, epgSourceId, epgChannelId, streamIds: [...] }]` — un evento
 * va in onda su più emittenti, e ognuna può corrispondere a più voci del catalogo (l'alta definizione
 * e la normale). Questo lettore faceva `channels.map(String)`, cioè convertiva quegli **oggetti** in
 * stringhe: `channelIds` conteneva `"[object Object]"`, e il player non aveva mai avuto in mano un
 * identificativo con cui aprire un canale.
 *
 * Non se n'era accorto nessuno perché le prove costruivano `SportEvent` a mano, con `channelIds`
 * già pronti: nessuna faceva passare questo lettore su una risposta vera. È la terza volta che lo
 * stesso meccanismo nasconde un difetto di contratto.
 *
 * Un elenco vuoto è **legittimo** e non un errore: la guida sa che quel canale trasmette la partita,
 * ma l'operatore non lo vende. Il player mostra il nome senza il pulsante per guardare.
 */
function playableChannelIds(raw: unknown): ReadonlyArray<string> {
  if (!Array.isArray(raw)) return [];
  const ids: string[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'object' || entry === null) continue;
    const streamIds = (entry as Record<string, unknown>)['streamIds'];
    if (!Array.isArray(streamIds)) continue;
    for (const id of streamIds) {
      if (typeof id === 'string' && id !== '') ids.push(id);
    }
  }
  // Lo stesso canale del catalogo può stare sotto due emittenti diverse: si mostra una volta.
  return [...new Set(ids)];
}

export function parseEventsResult(body: unknown): EventsResult {
  const root = asRecord(body);
  const list = root['events'];
  if (!Array.isArray(list)) fail('app.malformed_response', true, { field: 'events' });
  const events: SportEvent[] = list.map((raw) => {
    const item = asRecord(raw);
    return {
      id: String(item['id'] ?? ''),
      title: String(item['title'] ?? ''),
      competition: String(item['competition'] ?? ''),
      home: typeof item['home'] === 'string' ? item['home'] : null,
      away: typeof item['away'] === 'string' ? item['away'] : null,
      channelIds: playableChannelIds(item['channels']),
      startMs: readInstant(item['start']),
      endMs: readInstant(item['end']),
      // Vuote quando la guida non ha trovato il programma, e **vuote sul serio**: non
      // copiate da start/end, altrimenti il player non potrebbe più distinguere «la guida
      // lo sa» da «l'ho stimato io» (05-CONTRATTO-API §6.2).
      epgStartMs: optionalInstant(item['epgStart']),
      epgEndMs: optionalInstant(item['epgEnd']),
    };
  });
  return { nowMs: readServerNow(root), events };
}

function readInstant(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  fail('app.malformed_response', true, { field: 'instant' });
}

function optionalInstant(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

/**
 * Il contenuto di un aggregatore.
 *
 * Riusa `parseEventsResult` per la parte eventi, di proposito: le programmazioni arrivano nella stessa
 * forma degli eventi dello scraper, quindi **un lettore solo**. Due lettori per la stessa forma sono
 * due posti da correggere quando il contratto cambia, e uno dei due resta indietro.
 */
/**
 * Un testo localizzato: `{"it": "Calcio", "en": "Football"}`.
 *
 * Le chiavi che non sono una delle sette lingue si **saltano**, e i valori che non sono stringhe
 * pure. Non si rifiuta la risposta: una lingua in più pubblicata da un backend più nuovo non è un
 * errore — è esattamente il caso per cui il tipo è aperto — e far fallire la home per questo
 * significherebbe che aggiungere una lingua spegne le app vecchie.
 */
function parseLocalizedText(raw: unknown): LocalizedText {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Record<string, string> = {};
  for (const [locale, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value !== 'string' || value === '') continue;
    if (!(TEXT_LOCALES as ReadonlyArray<string>).includes(locale)) continue;
    out[locale] = value;
  }
  return out as LocalizedText;
}

/**
 * La vetrina.
 *
 * ## Quel che non si conosce si salta, non fa fallire
 *
 * Una fascia di un tipo che questa versione non sa disegnare non è un errore del server: è un'app
 * vecchia davanti a un backend nuovo, che su un parco di televisori che si aggiornano quando gli pare
 * è la condizione **normale**. Qui si legge tutto; è chi disegna a saltare quel che non conosce. Il
 * contrario — rifiutare la risposta — vorrebbe dire che pubblicare una fascia nuova spegne la home a
 * chi non ha ancora aggiornato.
 *
 * Restano invece rifiutate le righe **senza tipo** o **senza identificativo**: quelle non sono
 * estensioni, sono dati rotti, e tenerle vorrebbe dire disegnare piastrelle che non portano da
 * nessuna parte.
 */
export function parseStorefront(body: unknown): Storefront {
  const root = asRecord(body);

  const rawModules = root['modules'];
  if (rawModules !== undefined && rawModules !== null && !Array.isArray(rawModules)) {
    fail('app.malformed_response', true, { field: 'modules' });
  }
  const modules: StorefrontModule[] = [];
  for (const raw of Array.isArray(rawModules) ? rawModules : []) {
    const row = asRecord(raw);
    const type = row['type'];
    if (typeof type !== 'string' || type === '') continue;
    const params = row['params'];
    modules.push({
      type,
      title: parseLocalizedText(row['title']),
      params:
        params !== null && typeof params === 'object' && !Array.isArray(params)
          ? (params as Record<string, unknown>)
          : {},
    });
  }

  const heroes: StorefrontHero[] = [];
  for (const raw of Array.isArray(root['heroes']) ? root['heroes'] : []) {
    const row = asRecord(raw);
    const kind = row['kind'];
    const ref = row['ref'];
    // Un hero senza riferimento è un riquadro a tutta larghezza che non porta da nessuna parte: il
    // posto più visibile dello schermo occupato da un errore silenzioso.
    if (typeof kind !== 'string' || kind === '') continue;
    if (typeof ref !== 'string' || ref === '') continue;
    heroes.push({ kind, ref, image: typeof row['image'] === 'string' ? row['image'] : null });
  }

  const aggregators: StorefrontAggregator[] = [];
  for (const raw of Array.isArray(root['aggregators']) ? root['aggregators'] : []) {
    const row = asRecord(raw);
    const id = row['id'];
    if (typeof id !== 'string' || id === '') continue;
    aggregators.push({
      id,
      label: parseLocalizedText(row['label']),
      color: typeof row['color'] === 'string' && row['color'] !== '' ? row['color'] : '#0069A8',
      imageUrl: typeof row['imageUrl'] === 'string' && row['imageUrl'] !== '' ? row['imageUrl'] : null,
    });
  }

  /*
    Le tipologie di evento dell'installazione. Assenti = backend o gateway più vecchi del campo, e
    l'elenco vuoto è la risposta giusta: vedi `Storefront.eventKinds`.

    Si normalizzano in minuscolo perché è così che il player compone la chiamata, e il pannello
    accetta il codice come lo scrive l'operatore.
  */
  const eventKinds: string[] = [];
  for (const raw of Array.isArray(root['eventKinds']) ? root['eventKinds'] : []) {
    if (typeof raw !== 'string') continue;
    const code = raw.trim().toLowerCase();
    if (code !== '' && !eventKinds.includes(code)) eventKinds.push(code);
  }

  return {
    inheritedFrom: typeof root['inheritedFrom'] === 'string' ? root['inheritedFrom'] : '',
    modules,
    heroes,
    aggregators,
    eventKinds,
  };
}

export function parseAggregatorContent(body: unknown): AggregatorContentResult {
  const root = asRecord(body);
  const { nowMs, events } = parseEventsResult(body);

  const channels = Array.isArray(root['channels'])
    ? root['channels'].filter((id): id is string => typeof id === 'string' && id !== '')
    : [];

  const rawOnDemand = root['onDemand'];
  if (rawOnDemand !== undefined && rawOnDemand !== null && !Array.isArray(rawOnDemand)) {
    fail('app.malformed_response', true, { field: 'onDemand' });
  }
  const onDemand = (Array.isArray(rawOnDemand) ? rawOnDemand : []).map((raw) => {
    const item = asRecord(raw);
    // Un tipo che non conosciamo **non** diventa un film: sarebbe una piastrella che apre la
    // schermata sbagliata, e un rifiuto che si vede costa meno di una che porta nel posto errato.
    //
    // Scritto come espressione e non come guardia: `item['kind']` è `unknown`, e confrontarlo con
    // due letterali non lo restringe al loro tipo unione — TypeScript lo lascerebbe `string`.
    const kind: 'movie' | 'series' =
      item['kind'] === 'movie'
        ? 'movie'
        : item['kind'] === 'series'
          ? 'series'
          : fail('app.malformed_response', true, { field: 'onDemand.kind' });
    const ref = item['ref'];
    if (typeof ref !== 'string' || ref === '') {
      fail('app.malformed_response', true, { field: 'onDemand.ref' });
    }
    return {
      kind,
      ref,
      title: String(item['title'] ?? ''),
      image: typeof item['image'] === 'string' && item['image'] !== '' ? item['image'] : null,
    };
  });

  return { nowMs, events, channels, onDemand };
}

/** Il livello compatibile manda `"0"` come stringa dove va un numero: si legge, non si
 *  finge che non succeda. */
function compatFlag(value: unknown): boolean {
  return value === 1 || value === '1' || value === true;
}

/**
 * Una stringa non vuota, oppure `null`.
 *
 * Il catalogo dichiara i campi assenti in tre modi diversi — mancante, `null`, stringa vuota — e chi
 * disegna non deve distinguerli: per lui «non c'è» è una cosa sola.
 */
function nonEmpty(value: unknown): string | null {
  const text = typeof value === 'string' ? value.trim() : '';
  return text === '' ? null : text;
}

export function parseChannels(body: unknown): ReadonlyArray<Channel> {
  if (!Array.isArray(body)) fail('app.malformed_response', true, { field: 'streams' });
  const channels: Channel[] = [];
  for (const raw of body) {
    const item = asRecord(raw);
    const id = String(item['stream_id'] ?? '');
    if (id === '') continue;
    channels.push({
      id,
      number: Number(item['num'] ?? 0) || 0,
      name: String(item['name'] ?? ''),
      // `stream_icon` arrivava e veniva scartato: la guida disegnava le iniziali su ogni riga anche
      // dove il logo c'era. Vuoto diventa `null`, perché una stringa vuota in un `src` fa chiedere
      // al browser la pagina corrente e mostra l'icona dell'immagine rotta.
      logoUrl: nonEmpty(item['stream_icon']),
      categoryId: String(item['category_id'] ?? ''),
      hasArchive: compatFlag(item['tv_archive']),
      /*
        Le **ore**, dal campo nostro; i giorni di Xtream solo come ripiego.

        `tv_archive_hours` lo aggiunge il nostro backend accanto al campo standard, che resta in
        giorni per i lettori di terzi. Il ripiego moltiplica i giorni per 24, e serve a un gateway
        che non conosca ancora il campo nuovo: senza, l'aggiornamento dell'app prima di quello del
        backend spegnerebbe il catch-up su tutti i canali.
      */
      archiveHours:
        Number(item['tv_archive_hours'] ?? 0) || Number(item['tv_archive_duration'] ?? 0) * 24 || 0,
      isAdult: compatFlag(item['is_adult']),
      /*
        Assente vuol dire **falso**, ed è la scelta giusta per il verso sbagliato.

        Un gateway più vecchio del campo non lo manda: `compatFlag` risponde falso, e il giro di fondo
        finirebbe per non chiedere la guida di nessuno — cioè la guida sparirebbe dopo un aggiornamento
        dell'app. Per questo chi filtra (`LiveHost`) tratta «nessun canale dichiara `has_epg`» come «il
        gateway non lo conosce» e li chiede tutti: si perde l'economia, non la guida.

        Il ripiego non sta qui perché qui si legge **un canale per volta**, e la domanda «nessuno ce
        l'ha?» si può fare solo sull'elenco intero.
      */
      hasEpg: compatFlag(item['has_epg']),
    });
  }
  return channels;
}

/**
 * Le categorie, dalla forma del livello compatibile.
 *
 * Una voce senza identificativo o senza nome si **salta**: metterla nell'elenco darebbe una riga
 * vuota su cui il cursore si può fermare e che non dice niente — peggio dell'UUID che stiamo
 * togliendo, perché almeno quello si riconosceva come un difetto.
 */
export function parseCategories(body: unknown): ReadonlyArray<ChannelCategory> {
  if (!Array.isArray(body)) fail('app.malformed_response', true, { field: 'categories' });
  const categories: ChannelCategory[] = [];
  for (const raw of body) {
    const item = asRecord(raw);
    const id = nonEmpty(item['category_id']) ?? nonEmpty(String(item['category_id'] ?? ''));
    const name = nonEmpty(item['category_name']);
    if (id === null || name === null) continue;
    /*
      `parent_id`: `0` è la convenzione Xtream per «nessun padre», e assente vale lo stesso — è un
      gateway più vecchio del campo. Un padre che punta a **sé stessi** si scarta: sarebbe un anello
      nell'albero, e chi lo percorre non si ferma più.
    */
    const rawParent = item['parent_id'];
    const parent = rawParent === undefined || rawParent === null ? '' : String(rawParent).trim();
    const parentId = parent === '' || parent === '0' || parent === id ? null : parent;
    categories.push({ id, name, parentId });
  }
  return categories;
}

/**
 * Un anno, da un campo che lo scrive in tre modi.
 *
 * Il listino manda un numero (`year: 2024`), una stringa (`"2024"`), oppure `releaseDate` — che su
 * cataloghi diversi è `"2024"`, `"2024-03-11"` o vuoto. Zero quando non si capisce: **non** l'anno
 * corrente, che sarebbe un'invenzione con l'aria di un dato e metterebbe in cima ai «più recenti»
 * tutto quello di cui non si sa niente.
 */
function readYear(...candidates: ReadonlyArray<unknown>): number {
  for (const candidate of candidates) {
    if (typeof candidate === 'number' && Number.isInteger(candidate) && candidate > 1800) {
      return candidate;
    }
    if (typeof candidate === 'string') {
      const match = /\b(1[89]\d{2}|20\d{2})\b/.exec(candidate);
      if (match) return Number(match[1]);
    }
  }
  return 0;
}

/**
 * Se quel che è arrivato è una pagina web invece di un JSON.
 *
 * Si guardano i primi caratteri non vuoti, non tutto il corpo: su un catalogo da quattro megabyte
 * una ricerca sull'intero testo costerebbe a ogni chiamata per un caso che è sempre evidente
 * all'inizio.
 */
function looksLikeHtml(text: string): boolean {
  const head = text.slice(0, 200).trim().toLowerCase();
  return head.startsWith('<!doctype html') || head.startsWith('<html') || head.startsWith('<head');
}

/** Un unix in secondi, in millisecondi. `null` quando il campo manca o non è un istante. */
function readUnixSeconds(value: unknown): number | null {
  const seconds = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  return Math.round(seconds * 1000);
}

/**
 * I film, da `get_vod_streams`.
 *
 * `genre` e `year` li aggiunge il nostro backend al listino, accanto ai campi standard, perché
 * altrimenti starebbero solo sulla scheda: raggruppare per genere e ordinare per anno costerebbe una
 * chiamata per piastrella. Quando non ci sono — un gateway più vecchio dell'app — si resta senza,
 * e la vetrina si regge sulle categorie, che ci sono comunque.
 */
export function parseMovies(body: unknown): ReadonlyArray<VodItem> {
  if (!Array.isArray(body)) fail('app.malformed_response', true, { field: 'vod_streams' });
  const items: VodItem[] = [];
  for (const raw of body) {
    const item = asRecord(raw);
    const id = String(item['stream_id'] ?? '');
    if (id === '') continue;
    const categoryId = String(item['category_id'] ?? '');
    items.push({
      id,
      kind: 'movie',
      title: String(item['name'] ?? ''),
      genre: nonEmpty(item['genre']) ?? '',
      // La famiglia su cui pesano i consigliati è la **categoria**: vedi `VodItem.genreFamily`.
      genreFamily: categoryId,
      year: readYear(item['year'], item['releaseDate'], item['release_date']),
      posterUrl: nonEmpty(item['stream_icon']) ?? nonEmpty(item['cover']),
      categoryId,
      // Un film non ha stagioni, e l'elenco vuoto è ciò che `watchStateOf` legge come «è un film».
      episodesPerSeason: [],
      addedAtMs: readUnixSeconds(item['added']),
      isAdult: compatFlag(item['is_adult']),
    });
  }
  return items;
}

/**
 * Le serie, da `get_series`.
 *
 * `episodesPerSeason` resta **vuoto**, e non è un dato mancante per sbaglio: le stagioni stanno in
 * `get_series_info`, cioè una chiamata per serie. `withSeasons` le innesta quando si apre la scheda.
 */
export function parseSeriesList(body: unknown): ReadonlyArray<VodItem> {
  if (!Array.isArray(body)) fail('app.malformed_response', true, { field: 'series' });
  const items: VodItem[] = [];
  for (const raw of body) {
    const item = asRecord(raw);
    const id = String(item['series_id'] ?? '');
    if (id === '') continue;
    const categoryId = String(item['category_id'] ?? '');
    items.push({
      id,
      kind: 'series',
      title: String(item['name'] ?? ''),
      genre: nonEmpty(item['genre']) ?? '',
      genreFamily: categoryId,
      year: readYear(item['year'], item['releaseDate'], item['release_date']),
      posterUrl: nonEmpty(item['cover']) ?? nonEmpty(item['stream_icon']),
      categoryId,
      episodesPerSeason: [],
      /*
        Le serie dichiarano `last_modified` e non `added`.

        Non sono la stessa cosa — una serie «modificata» ieri può essere in catalogo da due anni,
        perché ogni episodio nuovo la tocca — ma per «Ultimi arrivi» è la risposta migliore che il
        listino abbia, ed è anche quella che chi guarda si aspetta: una serie che ha appena aggiunto
        una puntata *è* una novità. Il nome del campo lo dice a chi legge qui; il resto dell'app vede
        un istante.
      */
      addedAtMs: readUnixSeconds(item['last_modified'] ?? item['added']),
      isAdult: compatFlag(item['is_adult']),
    });
  }
  return items;
}

/**
 * La scheda di una serie, da `get_series_info`.
 *
 * ## Due fonti per le stagioni, e si crede agli episodi
 *
 * La risposta porta `seasons` (un elenco descrittivo) **e** `episodes` (una mappa da numero di
 * stagione all'elenco dei suoi episodi). Le due possono non coincidere: `seasons` è quel che il
 * fornitore dichiara, `episodes` è quel che c'è. Qui si costruisce da `episodes`, perché una stagione
 * dichiarata e senza episodi è una pastiglia che si può premere e non apre niente — e una stagione
 * con episodi ma non dichiarata sparirebbe del tutto, che è il più grave dei due.
 */
/**
 * I fatti comuni a un film e a una serie, dal nodo `info` della scheda.
 *
 * Un lettore solo per entrambi, di proposito: `get_vod_info` e `get_series_info` portano gli stessi
 * campi con gli stessi nomi, e due lettori per la stessa forma sono due posti da correggere quando il
 * contratto cambia — con uno dei due che resta indietro.
 */
function parseTitleFacts(info: Record<string, unknown>): {
  plot: string;
  posterUrl: string | null;
  backdropUrl: string | null;
  year: number;
  genre: string;
  durationMin: number | null;
  rating: number | null;
  voteCount: number | null;
  tagline: string;
  director: string | null;
  cast: string | null;
} {
  const durationSeconds = Number(info['duration_secs'] ?? 0);
  const rating = Number(info['rating']);
  const votes = Number(info['vote_count']);
  return {
    plot: String(info['plot'] ?? info['description'] ?? ''),
    posterUrl: nonEmpty(info['cover']) ?? nonEmpty(info['cover_big']),
    /*
      L'immagine larga arriva in due forme, e sono due chiamate diverse.

      `get_vod_info` manda `movie_image`, una stringa; `get_series_info` manda `backdrop_path`, un
      **elenco** — è la forma di TMDB, che ne può avere più di una. Si prende la prima: una scheda ha
      una key art sola, e sceglierne una a caso fra quelle disponibili sarebbe un'immagine che cambia
      senza motivo fra due aperture.
    */
    backdropUrl:
      nonEmpty(info['movie_image']) ??
      (Array.isArray(info['backdrop_path']) ? nonEmpty(info['backdrop_path'][0]) : null),
    year: readYear(info['year'], info['releasedate'], info['releaseDate'], info['release_date']),
    genre: nonEmpty(info['genre']) ?? '',
    durationMin:
      Number.isFinite(durationSeconds) && durationSeconds > 0 ? Math.round(durationSeconds / 60) : null,
    // `null` e non zero: un film senza voto e un film votato zero sono due cose diverse, e «⭐ 0,0»
    // su tutto il catalogo è peggio di niente.
    rating: Number.isFinite(rating) && rating > 0 ? rating : null,
    voteCount: Number.isFinite(votes) && votes > 0 ? votes : null,
    tagline: nonEmpty(info['tagline']) ?? '',
    director: nonEmpty(info['director']),
    // `actors` e `cast` portano la stessa cosa: i due nomi convivono nel livello compatibile, e
    // pannelli diversi riempiono ora l'uno ora l'altro.
    cast: nonEmpty(info['cast']) ?? nonEmpty(info['actors']),
  };
}

/** La scheda di un film, da `get_vod_info`. */
export function parseMovieDetail(body: unknown, movieId: string): MovieDetail {
  const root = asRecord(body);
  const info =
    typeof root['info'] === 'object' && root['info'] !== null
      ? (root['info'] as Record<string, unknown>)
      : {};
  const movieData =
    typeof root['movie_data'] === 'object' && root['movie_data'] !== null
      ? (root['movie_data'] as Record<string, unknown>)
      : {};
  return {
    id: movieId,
    // Il nome sta in `info` per lo standard e in `movie_data` per alcuni pannelli: si guardano
    // entrambi invece di scegliere e sperare.
    title: String(info['name'] ?? movieData['name'] ?? ''),
    ...parseTitleFacts(info),
  };
}

export function parseSeriesDetail(body: unknown, seriesId: string): SeriesDetail {
  const root = asRecord(body);
  const info = typeof root['info'] === 'object' && root['info'] !== null
    ? (root['info'] as Record<string, unknown>)
    : {};
  const rawEpisodes = root['episodes'];
  if (rawEpisodes !== undefined && rawEpisodes !== null && typeof rawEpisodes !== 'object') {
    fail('app.malformed_response', true, { field: 'episodes' });
  }

  const bySeason = new Map<number, EpisodeInfo[]>();
  const groups =
    typeof rawEpisodes === 'object' && rawEpisodes !== null
      ? (rawEpisodes as Record<string, unknown>)
      : {};

  for (const [key, value] of Object.entries(groups)) {
    if (!Array.isArray(value)) continue;
    for (const raw of value) {
      const entry = asRecord(raw);
      const id = String(entry['id'] ?? '');
      if (id === '') continue;
      /*
        Il numero di stagione dal **campo**, e la chiave della mappa solo come ripiego.

        Sono due dichiarazioni della stessa cosa, e quando divergono vince il campo dell'episodio:
        la chiave è un'etichetta di raggruppamento, `season` è ciò che il fornitore dice di
        quell'episodio. Prima il campo mancava del tutto nella nostra risposta — un episodio arrivava
        senza sapere in che stagione fosse, se non per la chiave sotto cui pendeva.
      */
      const season = Number(entry['season'] ?? key) || Number(key) || 1;
      const episodeNumber = Number(entry['episode_num'] ?? 0) || 0;
      if (episodeNumber <= 0) continue;
      const details = typeof entry['info'] === 'object' && entry['info'] !== null
        ? (entry['info'] as Record<string, unknown>)
        : {};
      const durationSeconds = Number(details['duration_secs'] ?? 0);
      const list = bySeason.get(season) ?? [];
      list.push({
        id,
        season,
        episode: episodeNumber,
        title: String(entry['title'] ?? details['name'] ?? ''),
        description: String(details['plot'] ?? ''),
        durationMin:
          Number.isFinite(durationSeconds) && durationSeconds > 0
            ? Math.round(durationSeconds / 60)
            : null,
        imageUrl: nonEmpty(details['movie_image']),
      });
      bySeason.set(season, list);
    }
  }

  const seasons: SeriesSeason[] = [...bySeason.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([number, episodes]) => ({
      number,
      episodes: [...episodes].sort((a, b) => a.episode - b.episode),
    }));

  return {
    id: seriesId,
    title: String(info['name'] ?? ''),
    ...parseTitleFacts(info),
    seasons,
  };
}

/**
 * La guida di molti canali, da `get_guide_window`.
 *
 * Il canale di ogni programma viene da **`stream_id` della riga**, non da un argomento: qui arrivano
 * i programmi di dodici canali mescolati, e attribuirli tutti allo stesso li disegnerebbe tutti
 * sulla stessa riga della guida — con le altre undici vuote, e nessun errore da nessuna parte.
 *
 * Una riga senza `stream_id` si salta. È un programma che non si sa dove mettere, e metterlo a caso
 * è peggio che perderlo: chi guarda lo leggerebbe come il palinsesto di un canale che non è il suo.
 */
export function parseGuideWindow(body: unknown): ReadonlyArray<Program> {
  const root = asRecord(body);
  const list = root['epg_listings'];
  if (!Array.isArray(list)) return [];

  const byChannel = new Map<string, unknown[]>();
  for (const raw of list) {
    const channelId = nonEmpty(asRecord(raw)['stream_id']);
    if (channelId === null) continue;
    const bucket = byChannel.get(channelId);
    if (bucket) bucket.push(raw);
    else byChannel.set(channelId, [raw]);
  }

  const programs: Program[] = [];
  for (const [channelId, rows] of byChannel) {
    for (const program of parsePrograms({ epg_listings: rows }, channelId)) programs.push(program);
  }
  return programs;
}

export function parsePrograms(body: unknown, channelId: string): ReadonlyArray<Program> {
  const root = asRecord(body);
  const list = root['epg_listings'];
  if (!Array.isArray(list)) return [];
  const programs: Program[] = [];
  for (const raw of list) {
    const item = asRecord(raw);
    const startMs = Number(item['start_timestamp'] ?? 0) * 1000;
    const endMs = Number(item['stop_timestamp'] ?? 0) * 1000;
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) continue;
    programs.push({
      id: String(item['id'] ?? `${channelId}-${startMs}`),
      channelId,
      title: decodeMaybeBase64(String(item['title'] ?? '')),
      // `category` del livello compatibile: «Sport», «Film», «News». Era scritto fisso a stringa
      // vuota mentre il dato esiste in `epg_programs.category` — il backend ora lo manda.
      genre: typeof item['category'] === 'string' ? item['category'] : '',
      startMs,
      endMs,
      /*
        **`eventKind` non viene da qui, e non ci verrà.**

        Ci avevo appoggiato la fascia «eventi» della home, confrontandola con la tipologia del
        modulo: essendo sempre `null` non corrispondeva mai, e la fascia spariva in silenzio.

        Gli eventi sportivi sono una sorgente a sé — `app:events:<tipologia>` in Redis, serviti da
        `GET /api/v1/app/events/:kind` — e un programma dell'EPG non sa di essere una partita. Resta
        `null` perché il campo è nel tipo per gli eventi che arrivano da lì, non per dedurlo da un
        palinsesto.
      */
      eventKind: null,
      description: decodeMaybeBase64(String(item['description'] ?? '')),
    });
  }
  return programs;
}

/** Il livello compatibile codifica titoli e descrizioni in base64. Quando non lo è, si
 *  tiene il testo com'è. */
function decodeMaybeBase64(value: string): string {
  if (value.length === 0 || value.length % 4 !== 0 || !/^[A-Za-z0-9+/]+={0,2}$/.test(value)) {
    return value;
  }
  try {
    const bytes = Uint8Array.from(atob(value), (character) => character.charCodeAt(0));
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return value;
  }
}
